'use client';

import { useDailyDraw, useRewardsProfile, useRolloverAt } from '@/lib/api/rewards-hub/hooks';
import { REWARDS_HUB_MOCK } from '@/lib/api/rewards-hub/mocks';
import { checkedInToday, todayCell } from '@/lib/api/rewards-hub/streak';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { usePathname, useRouter } from '@/lib/routing';
import { useDailyCardStore } from './dailyCardStore';
import { HUB_PATH, isDailyCardHiddenOn } from './routes';

/**
 * What the global daily card (floating widget, header icon) needs: whether
 * today's card is still waiting, which day it is, when the day turns over,
 * and what a press does. Reads the same queries as the hub's streak strip.
 */
export function useDailyCardOffer() {
  const { isAuthenticated } = useAuthContext();
  const pathname = usePathname();
  const router = useRouter();
  const setOpen = useDailyCardStore((s) => s.setOpen);
  const hosted = useDailyCardStore((s) => s.hosted);
  const dialogOpen = useDailyCardStore((s) => s.open);
  // Mock mode plays without a login, so the card can be tried on localhost.
  const canPlay = isAuthenticated || REWARDS_HUB_MOCK;
  const { data: profile } = useRewardsProfile(canPlay);
  const streak = profile?.streak;
  // `/daily` only when `/profile` cannot say whether today is done; a pick
  // still lands here, since the mutation writes the draw into the cache.
  const profileKnowsToday = streak?.checkedIn != null && streak?.picked != null;
  const { data: daily } = useDailyDraw(canPlay && Boolean(profile) && !profileKnowsToday);

  const pickedToday = daily?.pickedToday ?? streak?.picked ?? false;
  const checkedIn = daily ? checkedInToday(daily) : (streak?.checkedIn ?? false);
  const weekDay = daily?.streak?.weekDay ?? streak?.weekDay ?? 0;
  const nextAt = daily?.nextDrawAt ?? streak?.nextDayAt ?? null;
  const rareDay = Boolean(daily?.rareDay || streak?.rareDay);
  useRolloverAt(nextAt);

  // `hosted`: news/trade render the header but not the dialog. The card stays
  // while its dialog is open and goes with the close.
  const available = hosted && canPlay && Boolean(profile) && (!pickedToday || dialogOpen);
  // The page rule is for the floating card only; the header card shows wherever it can be taken.
  const allowedHere = !isDailyCardHiddenOn(pathname);

  // The rare-card day deals no daily table; its card is played on the hub.
  const open = () => {
    if (rareDay) {
      router.push(HUB_PATH);
      return;
    }
    setOpen(true);
  };

  return { available, allowedHere, day: todayCell(weekDay, checkedIn), nextAt, open };
}
